import {
  collection,
  onSnapshot,
  query,
  QueryConstraint,
  where,
} from "firebase/firestore";
import { useEffect, useState } from "react";

import { firestore } from "../../App/firebase";
import { useAppSelector } from "../../App/store";
import { CalendarItemInfo } from "./Item";

type OrderInfo = {
  calendarProps: CalendarItemInfo;
  created: number;
  maxIndex: number;
};

export default () => {
  const isAdmin = useAppSelector(
    (state) => state.auth.userdata?.admin === true
  );
  const uid = useAppSelector((state) => state.auth.user?.uid);

  const [oneDayDocs, setOneDayDocs] = useState<any[]>([]);
  const [startDayMatches, setStartDayMatches] = useState<any[]>([]);
  const [endDayMatches, setEndDayMatches] = useState<any[]>([]);

  const queryStartTime = new Date().getTime() - 86400000 * 5;

  const getQuery = (...consts: QueryConstraint[]) => {
    const col = collection(firestore, "forms_v2");

    if (isAdmin) {
      return query(col, ...consts);
    } else {
      return query(col, where("creator", "==", uid), ...consts);
    }
  };

  useEffect(() => {
    const q = getQuery(
      where("shipment.status", "in", ["date", "urgent"]),
      where("shipment.dateTimestamp", ">=", queryStartTime)
    );

    const listener = onSnapshot(q, (snap) => {
      setOneDayDocs(snap.docs);
    });

    return listener;
  }, [isAdmin, uid]);

  useEffect(() => {
    const q = getQuery(
      where("shipment.status", "==", "date-range"),
      where("shipment.dateStartTimestamp", ">=", queryStartTime)
    );

    const listener = onSnapshot(q, (snap) => {
      setStartDayMatches(snap.docs);
    });

    return listener;
  }, [isAdmin, uid]);

  useEffect(() => {
    const aMonthLater = new Date();
    aMonthLater.setUTCHours(0, 0, 0, 0);
    aMonthLater.setDate(aMonthLater.getDate() + 31);

    const q = getQuery(
      where("shipment.status", "in", ["date-range", "urgent"]),
      where("shipment.dateEndTimestamp", "<=", aMonthLater.getTime())
    );

    const listener = onSnapshot(q, (snap) => {
      setEndDayMatches(snap.docs);
    });

    return listener;
  }, [isAdmin, uid]);

  const pendingDocuments = [...oneDayDocs, ...startDayMatches, ...endDayMatches]
    .filter((x, i, a) => a.findIndex((y) => y.id === x.id) === i)
    .filter((x) => x.data().archived !== true);

  const pendingOrders: OrderInfo[] = pendingDocuments.map((doc, i) => {
    const data = doc.data();
    const shipment = data.shipment || {};

    const dateStart = new Date();
    const dateEnd = new Date();

    switch (shipment.status) {
      case "urgent":
        dateStart.setTime(shipment.dateEndTimestamp);
        dateEnd.setTime(shipment.dateEndTimestamp);
        break;

      case "date-range":
        dateStart.setTime(shipment.dateStartTimestamp);
        dateEnd.setTime(shipment.dateEndTimestamp);
        break;

      case "date":
        dateStart.setTime(shipment.dateTimestamp);
        dateEnd.setTime(shipment.dateTimestamp);
        break;
    }

    return {
      calendarProps: {
        dateStart,
        dateEnd,
        data,
        formId: doc.id,
      },
      created: i,
      maxIndex: 0,
    };
  });
  const dailyOrders = [...Array(35)].map((_, i) => {
    const dayOffset = i - 3;
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + dayOffset);

    const orders = pendingOrders
      .filter(
        (order) =>
          order.calendarProps.dateStart <= date &&
          order.calendarProps.dateEnd >= date
      )
      .sort((a, b) => a.created - b.created);

    return {
      date,
      orders,
    };
  });

  for (let i = 0; i < dailyOrders.length; i++) {
    const dayInfo = dailyOrders[i];

    for (let j = 0; j < dayInfo.orders.length; j++) {
      if (j > dayInfo.orders[j].maxIndex) {
        let existingOrder: OrderInfo | undefined | true = true,
          n = j;

        while (existingOrder) {
          existingOrder = dayInfo.orders.find((order) => order.maxIndex === n);
          dayInfo.orders[j].maxIndex = n;
          n++;
        }
      }
    }
  }

  const dailyItems = dailyOrders.map((dayInfo, i) => {
    const items: (CalendarItemInfo | null)[] = [];
    const dayMaxIndex = dayInfo.orders.reduce(
      (a, b) => Math.max(a, b.maxIndex),
      0
    );

    for (let j = 0; j <= dayMaxIndex; j++) {
      const order = dayInfo.orders.find((order) => order.maxIndex === j);

      items.push(order?.calendarProps || null);
    }

    return { date: dayInfo.date, items };
  });

  for (let w = 0; w < dailyItems.length / 7; w++) {
    const startDayIndex = w * 7;
    let nonEmptyMap = [];

    for (let i = 0; i < 7; i++) {
      const dayInfo = dailyItems[startDayIndex + i];

      if (dayInfo && dayInfo.items.find((v) => v)) {
        for (let j = 0; j < dayInfo.items.length; j++) {
          if (dayInfo.items[j]) {
            nonEmptyMap[j] = true;
          }
        }
      }
    }

    for (let i = 0; i < 7; i++) {
      const dayInfo = dailyItems[startDayIndex + i];

      if (dayInfo) {
        const newDayInfo: (CalendarItemInfo | null)[] = [];

        for (let j = 0; j < nonEmptyMap.length; j++) {
          if (nonEmptyMap[j]) {
            newDayInfo.push(dayInfo.items[j]);
          }
        }

        dailyItems[startDayIndex + i].items = newDayInfo;
      }
    }
  }

  return dailyItems;
};
